import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { getSurvey, submitSurvey } from '../lib/api';

function RatingInput({ question, value, onChange }) {
  const max = question.question_type === 'rating_10' ? 10 : 5;
  return (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
      {Array.from({ length: max }, (_, i) => i + 1).map(n => (
        <button
          key={n}
          onClick={() => onChange(n)}
          style={{
            width: 42, height: 42, borderRadius: 8, border: '2px solid',
            borderColor: value === n ? '#0176CE' : '#e2e8f0',
            background: value === n ? '#0176CE' : '#fff',
            color: value === n ? '#fff' : '#374151',
            fontWeight: 700, fontSize: 15, cursor: 'pointer',
          }}
        >{n}</button>
      ))}
    </div>
  );
}

function QuestionInput({ question, value, onChange }) {
  const { question_type, options } = question;

  if (question_type === 'rating_10' || question_type === 'rating_5') {
    return <RatingInput question={question} value={value?.answer_numeric} onChange={v => onChange({ answer_numeric: v, answer_text: String(v) })} />;
  }
  if (question_type === 'yes_no') {
    return (
      <div style={{ display: 'flex', gap: 12 }}>
        {['Yes', 'No'].map(opt => (
          <button key={opt} onClick={() => onChange({ answer_text: opt, answer_numeric: null })}
            style={{ padding: '10px 28px', borderRadius: 8, border: '2px solid', cursor: 'pointer', fontWeight: 600, fontSize: 14,
              borderColor: value?.answer_text === opt ? '#0176CE' : '#e2e8f0',
              background: value?.answer_text === opt ? '#0176CE' : '#fff',
              color: value?.answer_text === opt ? '#fff' : '#374151' }}>
            {opt}
          </button>
        ))}
      </div>
    );
  }
  if (question_type === 'multiple_choice' && options) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {options.map(opt => (
          <label key={opt} style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: 14, color: '#374151' }}>
            <input type="radio" name={`q_${question.question_id}`} checked={value?.answer_text === opt}
              onChange={() => onChange({ answer_text: opt, answer_numeric: null })}
              style={{ accentColor: '#0176CE', width: 16, height: 16 }} />
            {opt}
          </label>
        ))}
      </div>
    );
  }
  if (question_type === 'multiple_choice_multi' && options) {
    const selected = value?.answer_text ? value.answer_text.split('|') : [];
    function toggle(opt) {
      const next = selected.includes(opt) ? selected.filter(x => x !== opt) : [...selected, opt];
      onChange({ answer_text: next.join('|'), answer_numeric: null });
    }
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {options.map(opt => (
          <label key={opt} style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: 14, color: '#374151' }}>
            <input type="checkbox" checked={selected.includes(opt)} onChange={() => toggle(opt)}
              style={{ accentColor: '#0176CE', width: 16, height: 16 }} />
            {opt}
          </label>
        ))}
      </div>
    );
  }
  // open_text
  return (
    <textarea
      value={value?.answer_text || ''}
      onChange={e => onChange({ answer_text: e.target.value, answer_numeric: null })}
      rows={4}
      placeholder="Your answer…"
      style={{ width: '100%', padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, color: '#374151', resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit' }}
    />
  );
}

export default function SurveyPage() {
  const { token } = useParams();
  const [data, setData]       = useState(null);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted]   = useState(false);
  const [error, setError]           = useState('');

  useEffect(() => {
    getSurvey(token)
      .then(d => { setData(d); if (d.already_completed) setSubmitted(true); })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [token]);

  function setAnswer(qid, val) {
    setAnswers(a => ({ ...a, [qid]: val }));
  }

  async function handleSubmit() {
    // Validate required
    for (const q of (data?.questions || [])) {
      if (q.required && (!answers[q.question_id] || !answers[q.question_id].answer_text)) {
        setError(`Please answer: "${q.question_text}"`);
        return;
      }
    }
    setError('');
    setSubmitting(true);
    try {
      const payload = Object.entries(answers).map(([qid, ans]) => ({
        question_id:     parseInt(qid),
        answer_text:     ans.answer_text  || null,
        answer_numeric:  ans.answer_numeric != null ? ans.answer_numeric : null,
      }));
      await submitSurvey(token, payload);
      setSubmitted(true);
    } catch (e) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) return (
    <div style={s.outer}><div style={s.card}><p style={{ color: '#6b7280', textAlign: 'center' }}>Loading…</p></div></div>
  );

  if (error && !data) return (
    <div style={s.outer}><div style={s.card}>
      <div style={s.logo}>MasterFileAI</div>
      <p style={{ color: '#dc2626', textAlign: 'center' }}>{error}</p>
    </div></div>
  );

  if (submitted) return (
    <div style={s.outer}><div style={s.card}>
      <div style={s.logo}>MasterFileAI</div>
      <div style={{ textAlign: 'center', padding: '24px 0' }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>🙏</div>
        <h2 style={{ color: '#0a2540', fontWeight: 700, margin: '0 0 12px' }}>Thank you{data ? `, ${data.greeting_name}` : ''}!</h2>
        <p style={{ color: '#6b7280', fontSize: 15, lineHeight: 1.6 }}>Your feedback has been received.<br />We really appreciate you taking the time.</p>
      </div>
    </div></div>
  );

  return (
    <div style={s.outer}>
      <div style={s.card}>
        <div style={s.logo}>MasterFileAI</div>

        <h1 style={s.heading}>Hi {data.greeting_name},</h1>
        <p style={s.intro}>
          Thank you for taking the time to share your feedback — it means a lot to us and helps us improve.
          {data.file_name && <> This survey is about your recent file: <strong>{data.file_name}</strong>.</>}
        </p>
        {data.survey.description && <p style={s.desc}>{data.survey.description}</p>}

        <div style={{ marginTop: 32 }}>
          {data.questions.map((q, idx) => (
            <div key={q.question_id} style={s.qBlock}>
              <p style={s.qText}>
                <span style={s.qNum}>{idx + 1}</span>
                {q.question_text}
                {q.required && <span style={{ color: '#dc2626', marginLeft: 4 }}>*</span>}
              </p>
              <QuestionInput question={q} value={answers[q.question_id]} onChange={v => setAnswer(q.question_id, v)} />
            </div>
          ))}
        </div>

        {error && <div style={s.errorBox}>{error}</div>}

        <button onClick={handleSubmit} disabled={submitting} style={s.submitBtn}>
          {submitting ? 'Submitting…' : 'Submit Feedback'}
        </button>

        <p style={s.footer}>
          This survey was sent personally to {data.full_name} at {data.company_name}.
        </p>
      </div>
    </div>
  );
}

const s = {
  outer:     { minHeight: '100vh', background: '#f0f4f8', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '40px 16px' },
  card:      { background: '#fff', borderRadius: 16, boxShadow: '0 4px 24px rgba(0,0,0,0.08)', padding: '40px 44px', width: '100%', maxWidth: 640 },
  logo:      { background: '#0176CE', color: '#fff', fontWeight: 700, fontSize: 16, padding: '8px 16px', borderRadius: 8, display: 'inline-block', marginBottom: 28 },
  heading:   { fontSize: 26, fontWeight: 700, color: '#0a2540', margin: '0 0 12px' },
  intro:     { fontSize: 15, color: '#374151', lineHeight: 1.7, margin: '0 0 8px' },
  desc:      { fontSize: 14, color: '#6b7280', lineHeight: 1.6, background: '#f8fafc', borderRadius: 8, padding: '12px 16px', margin: '12px 0 0' },
  qBlock:    { marginBottom: 32 },
  qText:     { fontSize: 15, fontWeight: 600, color: '#0a2540', margin: '0 0 14px', lineHeight: 1.5, display: 'flex', alignItems: 'flex-start', gap: 10 },
  qNum:      { background: '#0176CE', color: '#fff', borderRadius: '50%', width: 22, height: 22, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, flexShrink: 0, marginTop: 1 },
  errorBox:  { background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, color: '#dc2626', padding: '12px 16px', marginBottom: 16, fontSize: 13 },
  submitBtn: { width: '100%', background: '#0176CE', border: 'none', borderRadius: 10, color: '#fff', cursor: 'pointer', fontSize: 16, fontWeight: 700, padding: '15px', marginTop: 8 },
  footer:    { fontSize: 12, color: '#9ca3af', textAlign: 'center', marginTop: 20, lineHeight: 1.5 },
};
